/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'./BoardModel'
], function(_, BoardModel) {
	class BoardModelFactory {

		constructor(options){
			this.productVersion = options.productVersion;
			this.glassFeature = options.glassFeature;
			this.dashboardAPI = options.dashboardAPI;
			this.boardId = options.boardId;
			this.prefetchBoardPromise = options.prefetchBoardPromise;
			this.boardSpec = options.boardSpec;
			this.widgetRegistry = options.widgetRegistry;
			this.extensions = options.extensions;
			this.stringResources = options.stringResources;
			this.eventRouter = options.eventRouter;
			this.logger = options.logger;
			this.createLayoutModel = options.createLayoutModel;
			this.glass =  this.dashboardAPI.getFeature('Glass');
		}

		createBoardModel() {
			return this._getBoardSpec(this.boardId)
				.then(boardSpec => this._upgradeSpec(boardSpec))
				.then( specInfo => {
					//If a dashboard is opened, set the saved instance with the spec that comes from cm.
					if (this._onLoadSpec) {
						this.dashboardAPI.getCurrentContentView().setSavedInstance(this._onLoadSpec);
					}
					return specInfo;
				})
				.then(this._createBoardModel.bind(this));
		}

		_createBoardModel(specInfo){
			const meta = specInfo.spec && specInfo.spec._meta;
			const id = meta && meta.bundleID || this.boardId;

			// ensure that the boardModel name matches the one in _meta that can be updated by rename on welcome
			// TODO: store the name in one place only
			const name = meta && meta.name;

			const boardModel = new BoardModel(
				specInfo.spec, {
					id: id,
					name: name,
					eventRouter: this.eventRouter,
					widgetRegistry: this.widgetRegistry,
					boardModelExtensions: this.extensions.getBoardModelExtensions(),
					layoutExtensions: this.extensions.getLayoutModelExtensions(),
					dashboardApi: this.dashboardAPI,
					logger: this.logger,
					createLayoutModel: this.createLayoutModel,
					isUpgraded: specInfo.upgraded
				}
			);

			const boardModelInfo =  {
				boardModel: boardModel,
				boardSpec: specInfo.spec,
				boardId: this.boardId
			};

			return boardModelInfo;
		}

		_getBoardSpec(boardId) {
			if (!this.boardId) {
				var moreButton = this.dashboardAPI.findGlassPlugin('com.ibm.bi.glass.common.operations');
				if (moreButton) {
					moreButton.disable();
				}
			}
			if (this.boardSpec) {
				return Promise.resolve(this.boardSpec);
			}
			//Fetch board spec via content service if it's not set
			return this.glass.getService('.DashboardContent')
				.then((contentService) => {
					if (this.prefetchBoardPromise) {
						return contentService.handlePrefetchedDashboard(this.prefetchBoardPromise)
							.catch(error => {
								this.logger.error(error);
								return contentService.getDashboard(boardId);
							});
					} else {
						return contentService.getDashboard(boardId);
					}
				})
				.then((dashboardInfo) => {
					return this._resolveBoardSpec(dashboardInfo);
				});
		}

		/**
		* Update the boardSpec from the responseData
		* @private
		* @param {Object} dashboardInfo
		* @example
			{
				boardSpec: {xxx},
				boardId: 'i12345678',
				permissions: ['execute','read','setPolicy','traverse', 'write'],
				searchPath: 'CAMID("LDAP:u:uid=hmiller,ou=people")/folder[@name=\'My Folders\']/exploration[@name=\'New dashboard1\']',
				name: 'New dashboard1'
			}
		* @returns Pormise<Object> Resolved with boardSpec
		*/
		_resolveBoardSpec ({ boardId, name, searchPath, permissions, boardSpec }) {
			boardSpec._meta = {
				bundleID: boardId
			};
			this.dashboardAPI.setPermissions(permissions);
			// Gemini expects the title to be in the spec. Get the default so that we can benefit from multilingual names,
			boardSpec.name = name;
			boardSpec.searchPath = searchPath;
			this.boardSpec = boardSpec;
			this._onLoadSpec = JSON.stringify(boardSpec);
			// in the case where we loaded the object from a url we don't have the ID. so we set it here
			this.boardId = boardId;
			return boardSpec;
		}

		_upgradeSpec(boardSpec) {
			return this.glass.getService('.UpgradeService').then(upgradeService => {
				boardSpec.version = boardSpec.version || upgradeService.getLatestDashboardSpecVersion();
				if ( upgradeService.needsUpgrade(boardSpec, null) ) {
					return this._doUpgrade(boardSpec, upgradeService).then((newSpec) => {
						return {
							spec: newSpec,
							upgraded: true
						};
					});
				} else {
					return {
						spec: boardSpec,
						upgraded: false
					};
				}
			}).catch((err) => {
				if (err.obj) {
					// recover
					return {
						spec: err.obj,
						upgraded: true
					};
				}
				// re-throw
				throw err;
			});
		}

		_doUpgrade(boardSpec, upgradeService) {
			const PROGRESS_FAST = 10, PROGRESS_SLOW = 100;
			let toast = null;
			const completeMessage = this.dashboardAPI.canAuthor() ? this.stringResources.get('toastUpgradeDone') : this.stringResources.get('upgradeComplete');
			const upgradeProgress = (data) => {
				toast.setComplete((data.completed / data.total) * 100, {
					isComplete: data.done,
					completeMsg: data.done ? completeMessage : null,
					duration: data.done ? PROGRESS_FAST : PROGRESS_SLOW
				});
			};


			const upgradeMessage = this.stringResources.get('upgradingDashboard', {
				name: boardSpec.name,
				version: this.productVersion
			});

			toast = this.glassFeature.showProgressToast(upgradeMessage, {
				noCancelBtn: true,
				noHideBtn: true
			});

			//If a previous upgrade failed (e.g. datasource not found) and dashboard spec now has widget models under layout, moving the internal
			//widget model from the layout to the root, so that the upgrade steps can work again when we relink
			boardSpec = this._restoreWidgetModelsForUpgrade(boardSpec, upgradeService.getLatestDashboardSpecVersion());

			return upgradeService.upgrade(boardSpec, null /*requestedVersion -> null means it's Infinity*/, {
				ajaxSvc: this.dashboardAPI.getFeature('Ajax'),
				dashboardApi: this.dashboardAPI,
				services: this.services,
				logger: this.logger,
				hasWrite: this.dashboardAPI.canAuthor(),
				progress: upgradeProgress,
				noToast: true,
				originalSpecVersion: boardSpec.version
			}).catch((err) => {
				toast.showButton('hide');
				toast.fail();
				throw err;
			});
		}

		_findWidgetInLayouts(layout, widgetLayouts) {
			if( layout && layout.type === 'widget' && layout.features && layout.features.Models_internal) {
				widgetLayouts.push(layout);
			}
			if(layout && layout.items && layout.items.length > 0) {
				for(let i = 0; i <layout.items.length; i++) {
					const item = layout.items[i];
					this._findWidgetInLayouts(item, widgetLayouts);
				}
			}
		}

		_restoreWidgetModelsForUpgrade(boardSpec, latestVersion) {
			let widgetLayouts = [];
			if(boardSpec.version < latestVersion ) {
				if(boardSpec.layout) {
					this._findWidgetInLayouts(boardSpec.layout, widgetLayouts);
					for(let i =0; i< widgetLayouts.length; i++) {
						const layout = widgetLayouts[i];
						const widget = layout.features.Models_internal;
						if(!boardSpec.widgets) {
							boardSpec.widgets = {};
						}
						boardSpec.widgets[layout.id] = widget;
						delete layout.features.Models_internal;
					}
				}
			}
			return boardSpec;
		}
	}

	/**
	 * factory method
	 * @param {object} - options
	 */
	BoardModelFactory.create = function(options) {
		return new BoardModelFactory(options);
	};


	return BoardModelFactory;
});
